import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, Sun, Moon } from 'lucide-react';
import NotificationBell from './NotificationBell';

interface AdminHeaderProps {
  onMenuClick: () => void;
  /**
   * Where the logo links. Defaults to the tenant admin console home; the
   * super-admin shell (SuperAdminLayout) reuses this component with
   * `homeHref="/accounts"` instead of forking a copy.
   */
  homeHref?: string;
}

/**
 * Header for the dedicated admin shell (AdminLayout, and reused by
 * SuperAdminLayout with a different `homeHref`). Free of learner chrome —
 * no BackgroundGenerationIndicator, which is a learner-consumption concern —
 * but it does carry the notifications bell: the backend already writes
 * admin-facing rows here (a join request notifies every active admin in-app and
 * by email), and without a bell in this shell those rows were unreachable
 * without switching back to the learner layout.
 *
 * The bell shows a count rather than a bare dot, and omits "view all" because
 * the admin shell has no notifications page of its own — see NotificationBell.
 */
export default function AdminHeader({ onMenuClick, homeHref = '/admin/enterprise/dashboard' }: AdminHeaderProps) {
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Initialize dark mode from localStorage (same key/logic as the learner header)
  useEffect(() => {
    const isDark = localStorage.getItem('theme') === 'dark' ||
      (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches);
    setIsDarkMode(isDark);
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const toggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('theme', newMode ? 'dark' : 'light');
    if (newMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-white dark:bg-tutara-dark-mode-surface border-b border-gray-200 dark:border-tutara-dark-mode-border">
      <div className="h-16 px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-full">
          {/* Left Section */}
          <div className="flex items-center">
            {/* Mobile Menu Button */}
            <button
              onClick={onMenuClick}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-tutara-dark-mode-bg"
            >
              <Menu className="h-6 w-6 text-tutara-charcoal dark:text-white" />
            </button>

            {/* Logo → admin home */}
            <Link to={homeHref} className="flex items-center ml-4 lg:ml-0">
              <img
                src="/logos/logo-primary.png?v=2"
                alt="Tutara"
                className="h-8 w-auto block dark:hidden"
              />
              <img
                src="/logos/logo-white.png?v=2"
                alt="Tutara"
                className="h-8 w-auto hidden dark:block"
              />
            </Link>
          </div>

          {/* Right Section */}
          <div className="flex items-center space-x-2">
            {/* Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              className="p-2 hover:bg-gray-100 dark:hover:bg-tutara-dark-mode-bg rounded-lg transition-colors"
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              title={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? (
                <Sun className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              ) : (
                <Moon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>

            {/* Notifications — join requests and other admin-facing events */}
            <NotificationBell showCount />
          </div>
        </div>
      </div>
    </header>
  );
}
